import { useState } from 'react';
import { Group } from '@mantine/core';
import { IconCirclePlusFilled } from '@tabler/icons-react';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { CrmExtensions } from '@ayocore/erp/crm/client/extensions';
import classes from './PartnerSelect.module.css';

interface PartnerSelectProps {
  value: string;
  onChange: (value: string | null) => void;
  label?: string;
  error?: React.ReactNode;
  disabled?: boolean;
  initialOption?: { value: string; label: string };
}

/**
 * PartnerSelect — селект партнёра с кнопкой быстрого создания.
 * Инкапсулирует состояние ключа перерендера, выбранной опции и модального окна создания.
 */
export function PartnerSelect({
  value,
  onChange,
  label: labelText = 'Партнер',
  error,
  disabled,
  initialOption,
}: PartnerSelectProps) {
  const partnerRepository = usePartnerRepository();
  const [selectKey, setSelectKey] = useState(0);
  const [selectedOption, setSelectedOption] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const createPartnerModal = useModal(CrmExtensions.Partners.createForm.get());

  const handlePartnerCreated = (newPartnerId?: number, partnerName?: string) => {
    setSelectKey(prev => prev + 1);
    if (typeof newPartnerId === 'number') {
      if (partnerName) {
        setSelectedOption({ value: String(newPartnerId), label: partnerName });
      }
      onChange(String(newPartnerId));
    }
    createPartnerModal.close();
  };

  const label = (
    <Group gap={4} align='center'>
      <span>{labelText}</span>
      {!disabled && (
        <span
          className={classes.addButton}
          onClick={() => {
            createPartnerModal.open({
              title: 'Создать нового партнера',
              onPartnerCreated: handlePartnerCreated,
            });
          }}
        >
          <IconCirclePlusFilled size={14} />
          <span className={classes.addLabel}>Создать нового</span>
        </span>
      )}
    </Group>
  );

  return (
    <RepositorySelect
      key={selectKey}
      label={label}
      repo={partnerRepository.orderBy('name', 'asc')}
      displayField='name'
      placeholder='Выберите партнёра...'
      initialLimit={10}
      searchLimit={10}
      enableServerSearch={true}
      searchDebounce={300}
      minSearchLength={2}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
      initialOption={selectedOption ?? initialOption}
    />
  );
}
